import { useEffect, useState } from "react";
import { Button, Input, Popover, Select, Tooltip } from "antd";
import { History, Play, Redo2, Save, Share2, Undo2 } from "lucide-react";
import { Link } from "react-router-dom";
import { getAccessibleProjects } from "../../../api/workflowServices";
import { duplicateOutputIds } from "../../../engine";
import { useBuilder } from "./BuilderContext";

const VISIBILITY_OPTIONS = [
  { value: "private", label: "Private · project members" },
  { value: "company", label: "Shared · whole organisation" },
];

// Live status of the preview run on the sample files.
const StatusPill = () => {
  const { state, run } = useBuilder();
  const { workflow, files } = state;
  if (!run || !Object.keys(files).length) {
    return <span className="rounded-full bg-gray-100 px-2.5 py-0.5 text-xs text-gray-500">No sample data</span>;
  }
  if (!run.ok) {
    const index = workflow.steps.findIndex((s) => s.id === run.error.stepId);
    return (
      <span className="flex items-center gap-1.5 rounded-full bg-red-50 px-2.5 py-0.5 text-xs text-red-700">
        <span className="h-1.5 w-1.5 rounded-full bg-red-500" /> Step {index + 1} has an error
      </span>
    );
  }
  const ms = run.results.reduce((sum, r) => sum + (r.durationMs || 0), 0);
  const last = run.results[run.results.length - 1];
  const rows = last ? last.rowsOut : run.tables[workflow.sources[0]?.id]?.rowCount;
  return (
    <span className="flex items-center gap-1.5 rounded-full bg-teal-50 px-2.5 py-0.5 text-xs text-teal-800">
      <span className="h-1.5 w-1.5 rounded-full bg-teal-600" /> Preview · {ms < 1000 ? `${Math.max(1, Math.round(ms))} ms` : `${(ms / 1000).toFixed(1)}s`} · {(rows ?? 0).toLocaleString()} rows out
    </span>
  );
};

const TopBar = ({ onSave, saving, canEdit, onRun, onVersions }) => {
  const { state, dispatch } = useBuilder();
  const { workflow, dirty } = state;
  const saved = Boolean(workflow._id);
  const nameClash = duplicateOutputIds(workflow).size > 0;
  const [projectName, setProjectName] = useState("");

  useEffect(() => {
    getAccessibleProjects()
      .then((response) => setProjectName(response.data?.find((p) => p._id === workflow.projectID)?.projectName || ""))
      .catch(() => {});
  }, [workflow.projectID]);

  const share = (
    <div className="w-72 space-y-3">
      <div>
        <div className="mb-1 text-xs font-medium text-gray-600">Who can see this workflow</div>
        <Select
          className="w-full"
          value={workflow.visibility || "private"}
          disabled={!canEdit}
          options={VISIBILITY_OPTIONS}
          onChange={(visibility) => dispatch({ type: "setMeta", changes: { visibility } })}
        />
        <div className="mt-1 text-[11px] text-gray-400">Save to apply.</div>
      </div>
    </div>
  );

  return (
    <header className="flex h-14 shrink-0 items-center gap-3 border-b border-gray-200 bg-white px-4">
      <Link to="/portal/workflows" className="flex items-center gap-2" title="All workflows">
        <span className="flex h-7 w-7 items-center justify-center rounded-md bg-gray-900 text-sm font-bold text-white">L</span>
        <span className="text-sm font-semibold text-gray-900">Ledgerate</span>
      </Link>
      <span className="text-gray-300">/</span>
      {projectName && (
        <Link to={`/portal/workflows?project=${workflow.projectID}`} className="text-sm text-gray-500 hover:text-gray-800">
          {projectName}
        </Link>
      )}
      {projectName && <span className="text-gray-300">—</span>}
      <Input
        variant="borderless"
        className="!w-auto min-w-[160px] max-w-[360px] !px-1 font-semibold"
        value={workflow.name}
        disabled={!canEdit}
        onChange={(e) => dispatch({ type: "setMeta", changes: { name: e.target.value } })}
      />
      <StatusPill />
      {(!saved || dirty) && <span className="text-xs text-amber-600">{saved ? "Unsaved changes" : "Not saved yet"}</span>}

      <div className="ml-auto flex items-center gap-1.5">
        <Tooltip title="Undo (⌘Z)">
          <Button type="text" icon={<Undo2 size={16} />} disabled={!state.past.length} onClick={() => dispatch({ type: "undo" })} />
        </Tooltip>
        <Tooltip title="Redo (⇧⌘Z)">
          <Button type="text" icon={<Redo2 size={16} />} disabled={!state.future.length} onClick={() => dispatch({ type: "redo" })} />
        </Tooltip>
        {saved && (
          <Button icon={<History size={14} />} onClick={onVersions}>
            History
          </Button>
        )}
        <Popover trigger="click" placement="bottomRight" content={share}>
          <Button icon={<Share2 size={14} />}>Share</Button>
        </Popover>
        {canEdit && (
          <Tooltip title="Save (⌘S)">
            <Button icon={<Save size={14} />} loading={saving} disabled={saved && !dirty} onClick={onSave}>
              Save
            </Button>
          </Tooltip>
        )}
        <Tooltip title={nameClash ? "Two outputs have the same file name. Rename one to run." : ""}>
          <Button type="primary" icon={<Play size={14} />} disabled={!workflow.sources.length || !workflow.outputs.length || nameClash} onClick={onRun}>
            Run flow
          </Button>
        </Tooltip>
      </div>
    </header>
  );
};

export default TopBar;
